// Pure helpers for "which BlinkScribe note belongs to the recording that just
// finished / to the visit open in the chart". No React, no network — unit
// tested under node (see the stale-note fix, 2026-09-28).
//
// Background: after a chart recording, the Edit window used to open the
// patient's PREVIOUS-visit note (an id cached before the recording existed),
// and the completion poll treated 'Submitted' as done and picked the
// patient-wide latest row. Providers edited that old note and the save
// re-posted it to the OLD Athena encounter.

// Status strings a recording/details row can carry that mean "the final note
// exists". 'Submitted' / 'Processing' are in flight; anything else
// ('Disconnected', failures) is not a finished note either.
const DONE_STATUSES = new Set(["SUMMARIZED", "APPROVED"]);
const IN_FLIGHT_STATUSES = new Set(["SUBMITTED", "PROCESSING"]);

const statusKey = (status) => String(status ?? "").trim().toUpperCase();

// True only for a finished note (Summarized or APPROVED).
export const isNoteDone = (status) => DONE_STATUSES.has(statusKey(status));

// True while the backend is still producing the note.
export const isNoteInFlight = (status) => IN_FLIGHT_STATUSES.has(statusKey(status));

// Appointment ids arrive as numbers, numeric strings, "" or null. Returns a
// canonical string ("" = unknown) so 887409, "887409" and " 887409 " compare
// equal.
export const normalizeApptId = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s || s === "0" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return "";
  if (/^\d+$/.test(s)) return String(Number(s));
  return s;
};

// Both known and equal.
export const sameAppointment = (a, b) => {
  const x = normalizeApptId(a);
  const y = normalizeApptId(b);
  return x !== "" && x === y;
};

// Both known and different. Unknown on either side is NOT a mismatch (nothing
// to compare against — e.g. a chart opened from search has no appointment).
export const appointmentMismatch = (chartApptId, noteApptId) => {
  const x = normalizeApptId(chartApptId);
  const y = normalizeApptId(noteApptId);
  return x !== "" && y !== "" && x !== y;
};

// recording/details rows expose the appointment as `appointmentid`; some
// callers historically read `config_appointmentid` or `config.appointmentid`.
export const rowAppointmentId = (row) =>
  normalizeApptId(
    row?.appointmentid ?? row?.config_appointmentid ?? row?.config?.appointmentid ?? ""
  );

// YYYY-MM-DD for `date` in the clinic's time zone. recordings.ts is stored in
// server-local time (America/Chicago), so the recording/details date filter
// must use that calendar day — toISOString() (UTC) asks for TOMORROW after
// 19:00 CDT and the poll never finds the row.
export const localDateStr = (date = new Date(), timeZone = "America/Chicago") => {
  const d = date instanceof Date ? date : new Date(date);
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const y = get("year"), m = get("month"), day = get("day");
    if (y && m && day) return `${y}-${m}-${day}`;
  } catch (_) { /* fall through to the browser's local day */ }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "Sep 21, 2026" for the first parseable value (appointment startdate, then
// the recording ts); "" if none. A bare YYYY-MM-DD is read as a local
// calendar day — new Date("2026-09-21") is UTC midnight and would print
// Sep 20 in Chicago.
export const visitDateLabel = (...candidates) => {
  for (const v of candidates) {
    if (v === null || v === undefined || v === "") continue;
    const s = String(v).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  }
  return "";
};

// Normalise a recording/details response (axios body or bare array).
export const rowsOf = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.data?.data)) return resp.data.data;
  return [];
};

const numId = (row) => {
  const n = Number(row?.id);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Snapshot taken when a recording STARTS: the highest recording id that
// already exists for the patient. 'Submitted' rows are ignored because
// recording/details unions in recordings_in_progress rows (a different id
// sequence) in that status; every other status comes from the recordings
// table, whose ids the finished note will also use. No rows → 0.
export const snapshotMaxId = (rows) => {
  let max = 0;
  for (const r of Array.isArray(rows) ? rows : []) {
    if (statusKey(r?.transcriptionstatus) === "SUBMITTED") continue;
    const id = numId(r);
    if (id !== null && id > max) max = id;
  }
  return max;
};

// The finished note for the recording that just stopped, or null.
//   - status Summarized/APPROVED only (never Submitted/Processing)
//   - id greater than the start snapshot (maxId === null means the snapshot
//     failed: any finished row qualifies, but the caller must not auto-open it)
//   - same appointment as the chart when the chart has one
//   - newest such row (a combine produces a newer row for the same visit)
export const pickCompletedRow = (rows, { maxId = null, appointmentId = "" } = {}) => {
  const appt = normalizeApptId(appointmentId);
  let best = null;
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!isNoteDone(r?.transcriptionstatus)) continue;
    const id = numId(r);
    if (id === null) continue;
    if (maxId !== null && maxId !== undefined && !(id > Number(maxId))) continue;
    if (appt && rowAppointmentId(r) !== appt) continue;
    if (!best || id > numId(best)) best = r;
  }
  return best;
};

// For the chart panel's visit-date query:
//   ready   — finished notes exist (usable = newest first, by ts then id)
//   pending — the day already has a row for the patient (Submitted,
//             Processing, Disconnected, ...) but no finished note: show
//             "Note processing…" and do NOT fall back to an older visit's
//             note (that fallback is what put last visit's note — and its id —
//             in front of the provider after a recording)
//   none    — no rows at all: the older-note fallback is allowed
export const classifyVisitRows = (rows) => {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const tsOf = (r) => {
    const t = new Date(r?.ts).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const byNewest = (a, b) => (tsOf(b) - tsOf(a)) || ((numId(b) || 0) - (numId(a) || 0));
  const usable = list.filter((r) => isNoteDone(r.transcriptionstatus)).sort(byNewest);
  if (usable.length) return { state: "ready", usable, pendingStatus: null };
  if (list.length) {
    const newest = [...list].sort(byNewest)[0];
    return {
      state: "pending",
      usable: [],
      pendingStatus: String(newest?.transcriptionstatus ?? "").trim() || null,
    };
  }
  return { state: "none", usable: [], pendingStatus: null };
};
